/**
 * Country settings — `/countries/:cc/admin/country-settings`. India go-live, cluster A3.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * WHY THIS SCREEN EXISTS, IN ONE MEASUREMENT
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * `POST /quotes/:id/pdf` refuses to print a country whose letterhead is still a
 * placeholder, and MEASURED on `crf_v3_ta` (14 Aug 2026) ALL SIX countries are — the
 * marker `[Registered address to be supplied …]` is on 6 of 6 `countries.address_block`
 * rows. India therefore cannot produce a quotation at all. The column that fixes it was
 * writable only by `PATCH /countries/:cc`, which had no user interface anywhere in the
 * product; five source files said so in terms. So the remedy for the one thing standing
 * between India and a customer document existed and nobody in the business could reach it.
 *
 * This is that surface, and it is deliberately SMALL: five columns. The two the letterhead
 * prints as identity, the tax registration number the ruling sequences next — and, since
 * the owner ruling of 2026-08-26, the country's own state pair, admitted VERBATIM as:
 *
 *     "ADD home_state_code to CountrySettingsPage. Approved as scope. Copy that points a
 *      coordinator at a page where the field does not exist is a dead end, and
 *      PATCH-API-writable only means the only fix path is a curl. Small, and it completes
 *      the sentence."
 *
 * The sentence it completes is C2's: the quote builder's SELLER_STATE_UNSET hint sends a
 * coordinator to "Country settings (state first, then the tax group)", and until this pair
 * existed that hint pointed at a screen with no such field. Currency, FY start, timezone,
 * sign-off policy and the deployment assignee are all on the same row and all deliberately
 * absent — none of them refuses a document, each has a different blast radius, and a
 * settings screen that offers everything is a screen where the one dangerous field is
 * indistinguishable from the eleven safe ones.
 *
 * ⚠ NO VALUE ON THIS SCREEN IS SUGGESTED, PREFILLED OR EXAMPLED. There is no placeholder
 * address in a `placeholder=`, no specimen GSTIN, no sample legal entity. A fabricated
 * registered address on a customer document is the exact defect the letterhead guard
 * exists to prevent, and an "e.g." in a form field is how a fabricated value gets typed.
 * The fields are empty-looking or hold what the database holds, and nothing else.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * THE THREE RULES THIS SCREEN IS BUILT ON
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * 1. IT SENDS A DIFF, NEVER A SNAPSHOT. The route's gate judges the fields the request
 *    SUPPLIES (`countries.service.ts` `suppliedLetterheadDefect`). Since every country
 *    holds a placeholder address today, a save that posted all three fields would drag
 *    that placeholder into a request that was only meant to set the GSTIN, and answer 422
 *    for a field the operator never touched. {@link changedFields} is that rule, and
 *    `country-settings.test.tsx` pins the body.
 *
 * 2. A REFUSAL LANDS UNDER THE FIELD IT IS ABOUT. The API answers 422
 *    `COUNTRY_LETTERHEAD_UNUSABLE` with `details.field` naming the COLUMN — `letterhead.ts`
 *    records why a column and not a screen: the mobile client and the agent call the same
 *    route and a web path would be true for one caller. Translating that column back to
 *    the input the operator is looking at is THIS screen's job, and {@link fieldOfRefusal}
 *    is the whole of it. A banner at the top of a form saying "address_block is
 *    unconfirmed" is a refusal nobody maps back.
 *
 * 3. SAVING IS NOT GOING LIVE, AND THE SUCCESS COPY MUST NOT IMPLY IT IS. A value that
 *    passes the write gate is a value that is not a placeholder and not blank. Whether it
 *    is the RIGHT address is a question no predicate can answer. The screen says the row
 *    was saved and points at the go-live checklist, which is the thing that judges.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Landmark } from 'lucide-react';
import { PageHeader } from '../../components/shell/AppShell';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  SkeletonRows,
  TextAreaField,
  TextField,
} from '../../components/ui';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { COUNTRIES } from '../../domain/countries';
import {
  COUNTRY_ADDRESS_MAX,
  COUNTRY_AGT_VALIDATION_NO_MAX,
  COUNTRY_CAE_MAX,
  COUNTRY_CITY_MAX,
  COUNTRY_COMMERCIAL_REGISTRY_MAX,
  COUNTRY_HOME_STATE_CODE_MAX,
  COUNTRY_HOME_STATE_NAME_MAX,
  COUNTRY_LEGAL_ENTITY_MAX,
  COUNTRY_POSTAL_CODE_MAX,
  COUNTRY_PROVINCE_MAX,
  COUNTRY_TAX_REGISTRATION_MAX,
  adminInfraKeys,
  exportSaftAo,
  fetchCountry,
  updateCountry,
  fetchPaymentSplit,
  updatePaymentSplit,
} from './infra-api';
import type { CountryPatchBody } from './infra-api';
import {
  changedFields,
  fieldOfRefusal,
  formOf,
  homeStatePairDefect,
} from './country-settings-model';
import type { CountryForm, EditableField } from './country-settings-model';
import { NoAdminAccess, errorSentence } from './states';

export function CountrySettingsPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can, isGroupAdmin } = useSession();
  const queryClient = useQueryClient();

  const permitted = isGroupAdmin || can(countryCode, 'config.manage');

  const [draft, setDraft] = useState<CountryForm | null>(null);
  const [fieldError, setFieldError] = useState<{ field: EditableField; message: string } | null>(
    null,
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const country = useQuery({
    queryKey: adminInfraKeys.country(countryCode),
    queryFn: ({ signal }) => fetchCountry(countryCode, signal),
    enabled: permitted,
  });

  const row = country.data;
  const original = row === undefined ? null : formOf(row);
  /*
   * ⚠ THE DRAFT IS SEEDED LAZILY FROM THE LOADED ROW, AND IT IS NEVER RE-SEEDED WHILE THE
   * OPERATOR IS TYPING. A `useEffect` that copied `row` into state on every successful
   * fetch would let a background refetch — react-query refetches on window focus — throw
   * away half-typed text the moment somebody alt-tabbed to look up the address they were
   * copying, which is precisely what a person filling this form does.
   */
  const form = draft ?? original;

  const save = useMutation({
    mutationFn: (body: CountryPatchBody) => updateCountry(countryCode, body),
    onSuccess: (updated) => {
      setDraft(formOf(updated));
      setFieldError(null);
      setActionError(null);
      setSaved(true);
      queryClient.setQueryData(adminInfraKeys.country(countryCode), updated);
      /*
       * ⚠ THE VERDICT IS RE-ASKED, NOT RECOMPUTED. `GET /countries/:cc/readiness` is the
       * judge of whether this country can issue a document; the go-live screen renders its
       * answer. Without this line an operator could fix the address here, walk to the
       * checklist, and be told the country still cannot print — from a cache.
       */
      void queryClient.invalidateQueries({
        queryKey: adminInfraKeys.countryReadiness(countryCode),
      });
    },
    onError: (error) => {
      setSaved(false);
      const under = fieldOfRefusal(error);
      if (under !== null) {
        setFieldError(under);
        setActionError(null);
      } else {
        setFieldError(null);
        setActionError(errorSentence(error, 'The country record could not be saved.'));
      }
    },
  });

  if (!permitted) return <NoAdminAccess />;

  const countryName = COUNTRIES[countryCode].name;
  /*
   * THE SAF-T(AO) SECTION IS KEYED ON THE ROW — `row.saft_ao`, the server's own regime
   * flag — and on nothing this client knows (`docs/ANGOLA-AGT-VALIDATION-DESIGN.md` §3 U0:
   * keyed on the country row, never on a country code). The directory's mirror of the same
   * flag (`domain/countries.ts` `saftAo`, parity-tested against the seed) is used ONLY for
   * the page subtitle, which renders before the row has loaded; the inputs wait for the row.
   */
  const fiscalIdentity = row?.saft_ao === true;
  const subtitleFiscal = COUNTRIES[countryCode].saftAo
    ? ' As a SAF-T (AO) country, its fiscal identity for AGT is also entered here.'
    : '';
  const body = original === null || form === null ? {} : changedFields(original, form);
  const dirty = Object.keys(body).length > 0;
  /*
   * The one refusal this form can decide from its own two inputs — a name with no code —
   * computed on every render so it appears the moment the pair goes inconsistent and
   * vanishes the moment it stops being true, with no state to fall out of date. While it
   * stands, Save is disabled: the request it describes is one the route refuses on the
   * merged row, so offering the button would be offering the 422.
   */
  const pairDefect = form === null ? null : homeStatePairDefect(form);

  const edit = (field: EditableField, value: string): void => {
    if (form === null) return;
    setDraft({ ...form, [field]: value });
    setSaved(false);
    if (fieldError?.field === field) setFieldError(null);
  };

  const errorFor = (field: EditableField): string | null => {
    if (fieldError?.field === field) return fieldError.message;
    // The client-side mirror ranks BELOW a server sentence: the server names the actual
    // refusal, and two messages under one field would bury it.
    if (pairDefect?.field === field) return pairDefect.message;
    return null;
  };

  return (
    <div className="flex flex-col gap-4 pb-6">
      <PageHeader
        title="Country settings"
        subtitle={`The letterhead ${countryName} prints on every quotation, its tax registration number, and this country's own state.${subtitleFiscal} A quotation cannot be produced while the registered address is still a placeholder.`}
      />

      <div className="flex flex-col gap-4 px-6">
        {actionError === null ? null : <Alert tone="danger">{actionError}</Alert>}

        {!saved ? null : (
          /*
           * ⚠ "SAVED", NOT "READY", AND `info` RATHER THAN A SUCCESS TONE — WHICH THE
           * PRIMITIVE HAPPENS TO SETTLE FOR US: `AlertTone` is `'danger' | 'warning' |
           * 'info'` and there is no green. That is the right answer here for the reason
           * the copy already has to obey. Passing the write gate means the value is not a
           * placeholder and not blank — nothing more. Whether it is the RIGHT address is
           * not a question this screen, or any predicate, can answer, so the sentence
           * points at the checklist that judges rather than making a claim of its own.
           */
          <Alert tone="info" title={`${countryName}'s record is saved`}>
            <p>
              {'What this country still needs before it can issue a document is on the '}
              <Link
                className="rounded-sm font-medium text-indigo hover:underline"
                to={`/countries/${countryCode}/admin/go-live`}
              >
                go-live checklist
              </Link>
              {'.'}
            </p>
          </Alert>
        )}

        {/* "letterhead and tax identity", because the card stopped being only the
          * letterhead when the GSTIN joined it and stopped being printable-identity-only
          * when the state pair did — a title narrower than its contents is how the state
          * fields become the thing nobody can find (the exact dead end the ruling names). */}
        {/*
          * v22-E3 — THE PAYMENT SPLIT, grp-only BY ABSENCE (the house rule: a
          * control a seat may not operate is not drawn). The server refuses
          * everyone but `config.global` holders regardless; this render
          * condition only keeps a Country Manager from meeting a card that
          * 403s. Ordinary config on this page stays `config.manage`.
          */}
        {isGroupAdmin ? <PaymentSplitCard countryCode={countryCode} /> : null}

        <Card title={`${countryName} letterhead and tax identity`}>
          {country.isPending ? (
            <SkeletonRows rows={4} label="Loading country settings" />
          ) : country.isError || form === null ? (
            <EmptyState
              icon={<Landmark aria-hidden="true" className="size-5" />}
              title="Country settings could not be loaded"
              description={errorSentence(country.error, 'The server did not answer.')}
            />
          ) : (
            <form
              className="flex max-w-2xl flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (!dirty || save.isPending || pairDefect !== null) return;
                save.mutate(body);
              }}
            >
              <TextField
                label="Legal entity name"
                value={form.legal_entity}
                maxLength={COUNTRY_LEGAL_ENTITY_MAX}
                error={errorFor('legal_entity')}
                onChange={(event) => edit('legal_entity', event.target.value)}
              />

              <TextAreaField
                label="Registered address"
                rows={5}
                value={form.address_block}
                maxLength={COUNTRY_ADDRESS_MAX}
                error={errorFor('address_block')}
                hint="Printed verbatim under the entity name. Line breaks are kept."
                onChange={(event) => edit('address_block', event.target.value)}
              />

              {/*
               * The LABEL comes from the server (`countries.tax_label`): GSTIN in India,
               * TRN in the UAE, VAT No. in Tanzania, NIF in Angola. A hard-coded word here
               * would be wrong in five countries out of six, and a six-entry table in the
               * client would be a second copy of a column that already ships on this very
               * payload.
               *
               * AND NO FIELD AT ALL WHEN THE LABEL IS EMPTY — the DRC, owner ruling N-53,
               * 2026-09-17: "there is no 'NIF' on Congolese documents in this product."
               * A country with no tax identifier has nothing to enter here; showing the
               * input under a blank label would invite a number no document can print.
               */}
              {row !== undefined && row.tax_label.trim() === '' ? null : (
                <TextField
                  label={`${row?.tax_label ?? 'Tax registration'} (tax registration number)`}
                  value={form.tax_registration}
                  maxLength={COUNTRY_TAX_REGISTRATION_MAX}
                  error={errorFor('tax_registration')}
                  className="font-mono"
                  onChange={(event) => edit('tax_registration', event.target.value)}
                />
              )}

              {/*
                * THE C2 COPY'S DESTINATION (owner ruling 2026-08-26, recorded verbatim in
                * this file's header). The builder's hint sends a coordinator here for
                * "the country's own state", so the fieldset wears that exact noun — the
                * FollowupPage legend pattern. The intro states the two things an operator
                * cannot infer from a pair of empty inputs: codes are what tax compares,
                * and the ordering rule the doors in `countries.service.ts` enforce. It
                * SUGGESTS NO VALUE (this screen's standing rule): naming Gujarat — or any
                * state — here is how a wrong state gets typed into six countries.
                *
                * The CLEAR path is deliberately not annotated with live data: whether an
                * active split-mode group holds this state shut lives in `tax_groups`,
                * which this page does not load. The server's 422 names the holding groups
                * and the remedy, and `fieldOfRefusal` lands that sentence VERBATIM under
                * the code field — `homeStatePairDefect`'s docblock argues the whole
                * decision.
                */}
              <fieldset className="mt-2 flex flex-col gap-4">
                {/* The FollowupPage legend pattern, verbatim classes — a legend inside a
                  * flex fieldset renders reliably only in this house shape. */}
                <legend className="pb-1.5 text-label font-medium text-fg">
                  This country&rsquo;s own state
                </legend>
                <p className="text-label text-fg-secondary">
                  Tax splits compare state CODES, never names: an Indian quote shows
                  CGST+SGST only when the customer&rsquo;s state code equals this one.
                  Order matters — state first, then the split-mode tax group; while such a
                  group is active, this state cannot be cleared.
                </p>
                <TextField
                  label="State code"
                  value={form.home_state_code}
                  maxLength={COUNTRY_HOME_STATE_CODE_MAX}
                  error={errorFor('home_state_code')}
                  className="font-mono"
                  inputMode="numeric"
                  onChange={(event) => edit('home_state_code', event.target.value)}
                />
                <TextField
                  label="State name"
                  value={form.home_state_name}
                  maxLength={COUNTRY_HOME_STATE_NAME_MAX}
                  error={errorFor('home_state_name')}
                  onChange={(event) => edit('home_state_name', event.target.value)}
                />
              </fieldset>

              {/*
                * FISCAL IDENTITY (AGT) — `docs/ANGOLA-AGT-VALIDATION-DESIGN.md` §3 U0, owner
                * "go" 2026-09-25. Rendered ONLY for a row under the SAF-T(AO) regime, by
                * ABSENCE otherwise (the house rule: a control a country cannot use is not
                * drawn — the same shape as the tax-registration field under an empty label).
                * The six inputs are the header columns migration 67 added; they ride the
                * same diff, the same PATCH and the same `country.updated` event as every
                * field above, and a refusal lands under its own input through
                * `fieldOfRefusal`. Nothing here prints on or blocks a quotation (U5; the
                * readiness item stays reported). Since U6 a blank registry or city blocks the
                * SAF-T export, and the intro says so (the XSD makes province and postal code
                * optional — written when stored, never required).
                *
                * IT SUGGESTS NO VALUE — this screen's standing rule. No specimen registry
                * number, no example CAE, and above all no example certificate number: a
                * number that is not AGT's would print as a false compliance line the day U5
                * lands. The helper under the last field says exactly that.
                */}
              {!fiscalIdentity ? null : (
                <fieldset className="mt-2 flex flex-col gap-4">
                  <legend className="pb-1.5 text-label font-medium text-fg">
                    Fiscal identity (AGT)
                  </legend>
                  <p className="text-label text-fg-secondary">
                    What the SAF-T (AO) file header carries beside the NIF and the registered
                    address. City, province and postal code are the parts of the address
                    entered separately — the address block above stays exactly as printed.
                    Nothing here prints on or blocks a quotation; the SAF-T (AO) export refuses
                    until the registry and city are filled (province and postal code are optional).
                  </p>
                  <TextField
                    label="Commercial registry"
                    value={form.commercial_registry}
                    maxLength={COUNTRY_COMMERCIAL_REGISTRY_MAX}
                    error={errorFor('commercial_registry')}
                    className="font-mono"
                    onChange={(event) => edit('commercial_registry', event.target.value)}
                  />
                  <TextField
                    label="City"
                    value={form.city}
                    maxLength={COUNTRY_CITY_MAX}
                    error={errorFor('city')}
                    onChange={(event) => edit('city', event.target.value)}
                  />
                  <TextField
                    label="Province"
                    value={form.province}
                    maxLength={COUNTRY_PROVINCE_MAX}
                    error={errorFor('province')}
                    onChange={(event) => edit('province', event.target.value)}
                  />
                  <TextField
                    label="Postal code"
                    value={form.postal_code}
                    maxLength={COUNTRY_POSTAL_CODE_MAX}
                    error={errorFor('postal_code')}
                    className="font-mono"
                    onChange={(event) => edit('postal_code', event.target.value)}
                  />
                  <TextField
                    label="CAE"
                    value={form.cae}
                    maxLength={COUNTRY_CAE_MAX}
                    error={errorFor('cae')}
                    className="font-mono"
                    inputMode="numeric"
                    onChange={(event) => edit('cae', event.target.value)}
                  />
                  <TextField
                    label="AGT validation number"
                    value={form.agt_validation_no}
                    maxLength={COUNTRY_AGT_VALIDATION_NO_MAX}
                    error={errorFor('agt_validation_no')}
                    className="font-mono"
                    onChange={(event) => edit('agt_validation_no', event.target.value)}
                  />
                  <p className="text-label text-fg-secondary">
                    Issued by AGT with the certificate; leave blank until then; nothing prints
                    without it.
                  </p>
                </fieldset>
              )}

              <div className="flex items-center gap-3">
                <Button type="submit" disabled={!dirty || save.isPending || pairDefect !== null}>
                  {save.isPending ? 'Saving…' : 'Save'}
                </Button>
                {dirty ? (
                  <Button
                    variant="quiet"
                    type="button"
                    onClick={() => {
                      setDraft(original);
                      setFieldError(null);
                      setActionError(null);
                    }}
                  >
                    Discard changes
                  </Button>
                ) : null}
              </div>
            </form>
          )}
        </Card>

        {/* Design §3 U6 — beside the fiscal identity it reads, for a regime row and a seat that
          * may see every figure in the file (`config.manage` is this page's own gate). */}
        {fiscalIdentity && can(countryCode, 'amounts.view') ? (
          <SaftExportCard countryCode={countryCode} />
        ) : null}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────── design U6 · the SAF-T (AO) export ── */

/**
 * The period and one button: the server builds the file, validates it against the schema,
 * keeps a copy, and answers the bytes — or refuses with a sentence naming what is missing,
 * shown verbatim. No type or field choice exists to offer (DE 74/19 10 c)).
 */
function SaftExportCard({ countryCode }: { countryCode: string }): React.JSX.Element {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const download = async (): Promise<void> => {
    setError(null);
    setDownloading(true);
    try {
      const file = await exportSaftAo(countryCode, { from, to });
      const url = URL.createObjectURL(file.blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = file.filename ?? `SAFT-AO_${countryCode}_${from}_${to}.xml`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(errorSentence(err, 'The SAF-T (AO) file could not be produced.'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card title="SAF-T (AO) export">
      <div className="flex max-w-2xl flex-col gap-3">
        {error === null ? null : <Alert tone="danger">{error}</Alert>}
        <p className="text-label text-fg-secondary">
          The file AGT asks for: every signed quotation dated in the period, with the customers
          and products they cite. One calendar year per file; a copy is kept on the server.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-44">
            <TextField label="From" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </div>
          <div className="w-44">
            <TextField label="To" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </div>
          <Button disabled={from === '' || to === '' || downloading} onClick={() => void download()}>
            {downloading ? 'Exporting…' : 'Export SAF-T (AO)'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

/* ───────────────────────────────────────── v22-E3 · the payment split card ── */

/**
 * The split's one knob. DATA, not code: percentages per country, 20/80 in CD
 * today because the owner will type 20 — never a DRC branch anywhere. The
 * balance is previewed as 100 − advance so the operator reads the whole split
 * before saving; clearing reverts the country to exactly today's behaviour.
 */
function PaymentSplitCard({ countryCode }: { countryCode: string }): React.JSX.Element {
  const queryClient = useQueryClient();
  const split = useQuery({
    queryKey: ['admin', 'payment-split', countryCode],
    queryFn: ({ signal }) => fetchPaymentSplit(countryCode, signal),
  });
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const value = draft ?? split.data?.advance_pct ?? '';
  const advanceNum = Number(value);
  const balancePreview =
    value !== '' && Number.isFinite(advanceNum) && advanceNum > 0 && advanceNum < 100
      ? (100 - advanceNum).toFixed(2)
      : null;

  const save = useMutation({
    mutationFn: (pct: number | null) => updatePaymentSplit(countryCode, pct),
    onSuccess: () => {
      setError(null);
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'payment-split', countryCode] });
      // Every CRF record in the country derives from this — re-read them too.
      void queryClient.invalidateQueries({ queryKey: ['crfs'] });
    },
    onError: (err: unknown) => {
      setError(errorSentence(err, 'The split could not be saved.'));
    },
  });

  return (
    <Card title={`Payment split in ${countryCode}`}>
      {split.isPending ? (
        <SkeletonRows rows={2} />
      ) : split.isError ? (
        <Alert tone="danger">
          {errorSentence(split.error, 'The split could not be read.')}
        </Alert>
      ) : (
        <div className="flex flex-col gap-3">
          {error === null ? null : <Alert tone="danger">{error}</Alert>}
          <p className="text-label text-fg-secondary">
            When set, every CRF with an amount shows advance and balance figures derived
            from it, with the balance due on delivery. This is SHOWN on records — nothing
            here tracks whether the balance was paid. Clearing it returns {countryCode} to
            exactly the behaviour it had before a split existed.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-40">
              <TextField
                label="Advance %"
                type="number"
                value={value}
                onChange={(event) => {
                  setDraft(event.target.value);
                }}
              />
            </div>
            <p className="pb-2 text-sm text-fg-secondary">
              {balancePreview === null
                ? 'Between 0 and 100, exclusive.'
                : `Balance ${balancePreview}% — due on delivery.`}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              disabled={save.isPending || balancePreview === null}
              onClick={() => {
                save.mutate(advanceNum);
              }}
            >
              Save split
            </Button>
            <Button
              variant="quiet"
              disabled={save.isPending || split.data?.advance_pct == null}
              onClick={() => {
                save.mutate(null);
              }}
            >
              Clear — no split
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

